/**
 * Logger utility for Hotelpay Link application
 * Provides consistent logging with environment-based configuration
 */

export enum LogLevel {
  ERROR = 0,   // Only errors
  WARN = 1,    // Errors and warnings
  INFO = 2,    // Normal operations, errors and warnings
  DEBUG = 3,   // Verbose logging
  TRACE = 4    // Full debug, everything
}

interface LoggerConfig {
  level: LogLevel;
  enabled: boolean;
}

// Default configurations by environment
const CONFIG: Record<string, LoggerConfig> = {
  'local': {
    level: LogLevel.TRACE,  // Most verbose in local
    enabled: true           // Enabled by default
  },
  'test': {
    level: LogLevel.DEBUG,
    enabled: true
  },
  'staging': {
    level: LogLevel.INFO,
    enabled: false          // Disabled by default in staging
  },
  'production': {
    level: LogLevel.ERROR,  // Only errors in production
    enabled: false          // Disabled by default in production
  }
};

/**
 * Get current environment from hostname
 */
function getEnvironment(): string {
  if (typeof window === 'undefined') return 'local'; // SSR or build time
  
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'local';
  } else if (hostname.includes('.test') || hostname.includes('-test')) {
    return 'test';
  } else if (hostname.includes('-staging') || hostname.includes('.staging.')) {
    return 'staging';
  }
  return 'production';
}

/**
 * Logger class with environment-specific behavior
 */
class Logger {
  private config: LoggerConfig;
  private env: string;

  constructor() {
    this.env = getEnvironment();
    this.config = CONFIG[this.env] || CONFIG.production;
  }

  /**
   * Enable or disable logging
   */
  public setEnabled(enabled: boolean): void {
    this.config.enabled = enabled;
  }

  /**
   * Set logging level
   */
  public setLevel(level: LogLevel): void {
    this.config.level = level;
  }

  /**
   * Get current configuration
   */
  public getConfig(): { level: LogLevel, enabled: boolean, environment: string } {
    return {
      ...this.config,
      environment: this.env
    };
  }

  /**
   * Log error messages - always logged to console.error
   */
  public error(...args: any[]): void {
    if (this.config.enabled && this.config.level >= LogLevel.ERROR) {
      console.error(...args);
    }
  }

  /**
   * Log warning messages
   */
  public warn(...args: any[]): void {
    if (this.config.enabled && this.config.level >= LogLevel.WARN) {
      console.warn(...args);
    }
  }

  /**
   * Log informational messages
   */
  public info(...args: any[]): void {
    if (this.config.enabled && this.config.level >= LogLevel.INFO) {
      console.info(...args);
    }
  }

  /**
   * Log debug messages
   */
  public debug(...args: any[]): void {
    if (this.config.enabled && this.config.level >= LogLevel.DEBUG) {
      console.debug(...args);
    }
  }

  /**
   * Log trace messages (most verbose)
   */
  public trace(...args: any[]): void {
    if (this.config.enabled && this.config.level >= LogLevel.TRACE) {
      console.log(...args);
    }
  }

  /**
   * Legacy log method - maps to trace level
   */
  public log(...args: any[]): void {
    this.trace(...args);
  }
}

// Create singleton instance
const logger = new Logger();

// For use in browser console to control logging
if (typeof window !== 'undefined') {
  (window as any).__hotelpayLogger = logger;
}

// Helper functions for easy use
export function disableAllLogs(): void {
  logger.setEnabled(false);
}

export function enableLogs(level: LogLevel = LogLevel.TRACE): void {
  logger.setEnabled(true);
  logger.setLevel(level);
}

export default logger;